import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from './Layout';
import { AIPremiumPageWall } from './AIPremiumGate';
import { isEventFeatureEnabled } from '../config/eventTypes';
import { getDashboardPath } from '../lib/authRouting';

export const ProtectedRoute = ({
  children,
  allowedRoles = [],
  requireAdminAccess = false,
  requireAiPremium = false,
  premiumFeatureName = 'this AI feature',
  requiredEventFeature = null,
  accountRoleOnly = false,
}) => {
  const {
    user,
    loading,
    hasAdminAccess,
    adminAccessLoading,
    effectiveRole,
    hasAiPremiumAccess,
    aiSubscriptionLoading,
    refreshAiSubscription,
    currentEvent,
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wedding-cream">
        <div className="animate-pulse">
          <div className="w-16 h-16 border-4 border-wedding-sage border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const routeRole = accountRoleOnly ? user.role : (effectiveRole || user.role);

  if (allowedRoles.length > 0 && !allowedRoles.includes(routeRole)) {
    return <Navigate to={getDashboardPath(routeRole)} replace />;
  }

  if (requireAdminAccess && adminAccessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wedding-cream">
        <div className="animate-pulse">
          <div className="w-16 h-16 border-4 border-wedding-sage border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (requireAdminAccess && !hasAdminAccess) {
    return <Navigate to={getDashboardPath(routeRole)} replace />;
  }

  if (
    requiredEventFeature
    && currentEvent
    && !isEventFeatureEnabled(currentEvent, requiredEventFeature)
  ) {
    return <Navigate to={getDashboardPath(routeRole)} replace />;
  }

  if (requireAiPremium && aiSubscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wedding-cream">
        <div className="h-16 w-16 animate-spin rounded-full border-4 border-wedding-sage border-t-transparent" />
      </div>
    );
  }

  if (requireAiPremium && !hasAiPremiumAccess) {
    return (
      <DashboardLayout>
        <AIPremiumPageWall
          featureName={premiumFeatureName}
          onRefresh={refreshAiSubscription}
          refreshing={aiSubscriptionLoading}
        />
      </DashboardLayout>
    );
  }

  return children;
};

export const PublicRoute = ({ children }) => {
  const { user, loading, effectiveRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wedding-cream">
        <div className="animate-pulse">
          <div className="w-16 h-16 border-4 border-wedding-sage border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (user) {
    const routeRole = effectiveRole || user.role;
    return <Navigate to={getDashboardPath(routeRole)} replace />;
  }

  return children;
};
